import React, { useEffect, useState } from 'react';
import supportRequestService from '../services/supportRequestService';
import './SupportRequests.css';

const STATUS_LABELS = {
  new: 'New',
  in_progress: 'In progress',
  resolved: 'Resolved',
};

const SupportRequests = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortDirection, setSortDirection] = useState('desc'); // desc = newest first
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const { requests: data } = await supportRequestService.getRequests(
          statusFilter ? { status: statusFilter } : {}
        );
        setRequests(data || []);
      } catch (err) {
        console.error('Failed to load support requests', err);
        const msg =
          err?.response?.data?.message ||
          'Failed to load support requests. Please try again.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [statusFilter]);

  const handleStatusChange = async (request, nextStatus) => {
    if (request.status === nextStatus) return;

    setError('');
    setSuccess('');
    setUpdatingId(request.id);

    try {
      await supportRequestService.updateStatus(request.id, nextStatus);
      setRequests((prev) =>
        prev.map((r) =>
          r.id === request.id ? { ...r, status: nextStatus } : r
        )
      );
      setSuccess('Support request updated.');
    } catch (err) {
      console.error('Failed to update request', err);
      const msg =
        err?.response?.data?.message ||
        'Failed to update request. Please try again.';
      setError(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const truncateMessage = (text, maxLength = 80) => {
    if (!text) return '';
    const str = String(text);
    if (str.length <= maxLength) return str;
    return `${str.slice(0, maxLength).trim()}…`;
  };

  const buildGmailReplyUrl = (req) => {
    if (!req?.email) return '#';
    const to = encodeURIComponent(req.email);
    const subject = encodeURIComponent(
      req.subject || 'Support request from Essentique'
    );
    const lines = [
      '',
      '',
      '--- Original request ---',
      `Name: ${req.name}`,
      `Email: ${req.email}`,
      `Phone: ${req.phone || '-'}`,
      `Order #: ${req.order_number || '-'}`,
      '',
      req.message || '',
    ];
    const body = encodeURIComponent(lines.join('\n'));
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;
  };

  const sortedRequests = [...requests].sort((a, b) => {
    const aTime = new Date(a.created_at).getTime();
    const bTime = new Date(b.created_at).getTime();
    if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
    return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
  });

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <div className="support-requests-page">
      <h1 className="page-title">Support Requests</h1>
      <p className="page-subtitle">
        View and track messages sent from the customer Help &amp; Support page.
      </p>

      <div className="support-requests-toolbar">
        <label>
          Status filter
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </label>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      {loading ? (
        <p>Loading support requests...</p>
      ) : requests.length === 0 ? (
        <p>No support requests found.</p>
      ) : (
        <div className="support-requests-table-wrapper">
          <table className="support-requests-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={toggleSortDirection}
                >
                  Created{' '}
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Subject</th>
                <th>Order #</th>
                <th>Message</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRequests.map((req) => (
                <tr key={req.id}>
                  <td>{formatDateTime(req.created_at)}</td>
                  <td>{req.name}</td>
                  <td>{req.email}</td>
                  <td>{req.phone || '-'}</td>
                  <td>{req.subject || '-'}</td>
                  <td>{req.order_number || '-'}</td>
                  <td className="support-requests-message-cell">
                    <div className="message-preview">
                      {truncateMessage(req.message)}
                    </div>
                  </td>
                  <td>
                    <select
                      value={req.status}
                      onChange={(e) =>
                        handleStatusChange(req, e.target.value)
                      }
                      disabled={updatingId === req.id}
                    >
                      <option value="new">{STATUS_LABELS.new}</option>
                      <option value="in_progress">
                        {STATUS_LABELS.in_progress}
                      </option>
                      <option value="resolved">
                        {STATUS_LABELS.resolved}
                      </option>
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setSelectedRequest(req)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedRequest && (
        <div className="support-request-modal-backdrop" onClick={() => setSelectedRequest(null)}>
          <div
            className="support-request-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Support request</h2>
              <button
                type="button"
                className="modal-close-button"
                onClick={() => setSelectedRequest(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-row">
                <span className="label">Created</span>
                <span>{formatDateTime(selectedRequest.created_at)}</span>
              </div>
              <div className="modal-row">
                <span className="label">Name</span>
                <span>{selectedRequest.name}</span>
              </div>
              <div className="modal-row">
                <span className="label">Email</span>
                <span>{selectedRequest.email}</span>
              </div>
              <div className="modal-row">
                <span className="label">Phone</span>
                <span>{selectedRequest.phone || '-'}</span>
              </div>
              <div className="modal-row">
                <span className="label">Subject</span>
                <span>{selectedRequest.subject || '-'}</span>
              </div>
              <div className="modal-row">
                <span className="label">Order #</span>
                <span>{selectedRequest.order_number || '-'}</span>
              </div>
              <div className="modal-row">
                <span className="label">Status</span>
                <span>{STATUS_LABELS[selectedRequest.status] || selectedRequest.status}</span>
              </div>
              <div className="modal-message">
                <span className="label">Message</span>
                <p>{selectedRequest.message}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSelectedRequest(null)}
              >
                Ok
              </button>
              {selectedRequest.email && (
                <a
                  href={buildGmailReplyUrl(selectedRequest)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Reply in Gmail
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportRequests;

